import React, { useState, useEffect } from 'react';
import { reportsAPI, auditsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  FileText,
  Download,
  BarChart2,
  Loader2,
  FileSpreadsheet,
  AlertTriangle,
} from 'lucide-react';

const ReportsPage = () => {
  const [audits, setAudits] = useState([]);
  const [selectedAudit, setSelectedAudit] = useState('');
  const [auditReport, setAuditReport] = useState(null);
  const [findingsRegister, setFindingsRegister] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('audit');

  useEffect(() => {
    fetchAudits();
    fetchFindingsRegister();
  }, []);

  useEffect(() => {
    if (selectedAudit) {
      fetchAuditReport();
    }
  }, [selectedAudit]);

  const fetchAudits = async () => {
    try {
      const res = await auditsAPI.getAll();
      setAudits(res.data);
    } catch (error) {
      console.error('Error fetching audits:', error);
    }
  };

  const fetchAuditReport = async () => {
    setLoading(true);
    try {
      const res = await reportsAPI.getAuditReport(selectedAudit);
      setAuditReport(res.data);
    } catch (error) {
      console.error('Error fetching audit report:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFindingsRegister = async () => {
    try {
      const res = await reportsAPI.getFindingsRegister();
      setFindingsRegister(res.data);
    } catch (error) {
      console.error('Error fetching findings register:', error);
    }
  };

  const downloadCSV = (data, filename) => {
    const headers = Object.keys(data[0] || {});
    const csvContent = [
      headers.join(','),
      ...data.map((row) =>
        headers.map((h) => `"${String(row[h] || '').replace(/"/g, '""')}"`).join(',')
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getStatusBadge = (status) => {
    const styles = {
      Compliant: 'badge-compliant',
      'Non-Compliant': 'badge-non-compliant',
      'Partially Compliant': 'badge-partial',
      Pending: 'badge-pending',
    };
    return <Badge className={styles[status] || 'badge-pending'}>{status}</Badge>;
  };

  const getSeverityBadge = (severity) => {
    const styles = {
      Critical: 'bg-red-100 text-red-800 border-red-200',
      High: 'bg-orange-100 text-orange-800 border-orange-200',
      Medium: 'bg-amber-100 text-amber-800 border-amber-200',
      Low: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    };
    return <Badge className={`${styles[severity]} border`}>{severity}</Badge>;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white border">
          <TabsTrigger value="audit" className="data-[state=active]:bg-[#4A5690] data-[state=active]:text-white">
            <FileText size={16} className="mr-2" />
            Audit Report
          </TabsTrigger>
          <TabsTrigger value="findings" className="data-[state=active]:bg-[#4A5690] data-[state=active]:text-white">
            <AlertTriangle size={16} className="mr-2" />
            Findings Register
          </TabsTrigger>
          <TabsTrigger value="consolidated" className="data-[state=active]:bg-[#4A5690] data-[state=active]:text-white">
            <BarChart2 size={16} className="mr-2" />
            Consolidated Report
          </TabsTrigger>
        </TabsList>

        {/* Audit Report Tab */}
        <TabsContent value="audit" className="space-y-6">
          <Card>
            <CardContent className="py-4">
              <div className="flex flex-wrap gap-4 items-end">
                <div className="flex-1 min-w-[250px]">
                  <Label className="text-xs text-slate-500 mb-1 block">Select Audit</Label>
                  <Select value={selectedAudit} onValueChange={setSelectedAudit}>
                    <SelectTrigger data-testid="select-audit-report">
                      <SelectValue placeholder="Choose an audit" />
                    </SelectTrigger>
                    <SelectContent>
                      {audits.map((audit) => (
                        <SelectItem key={audit.id} value={audit.id}>
                          {audit.audit_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {auditReport && (
                  <Button
                    variant="outline"
                    onClick={() =>
                      downloadCSV(auditReport.observations, `audit-report-${selectedAudit}`)
                    }
                    data-testid="download-audit-report"
                  >
                    <Download size={16} className="mr-2" />
                    Export CSV
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {loading ? (
            <Card>
              <CardContent className="py-16 text-center">
                <Loader2 className="h-8 w-8 animate-spin text-[#4A5690] mx-auto" />
              </CardContent>
            </Card>
          ) : auditReport ? (
            <>
              {/* Summary */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="metric-card">
                  <p className="metric-label">Total Observations</p>
                  <p className="metric-value text-[#4A5690]">{auditReport.summary.total}</p>
                </Card>
                <Card className="metric-card">
                  <p className="metric-label">Compliant</p>
                  <p className="metric-value text-emerald-500">{auditReport.summary.compliant}</p>
                </Card>
                <Card className="metric-card">
                  <p className="metric-label">Partially Compliant</p>
                  <p className="metric-value text-amber-500">
                    {auditReport.summary.partially_compliant}
                  </p>
                </Card>
                <Card className="metric-card">
                  <p className="metric-label">Non-Compliant</p>
                  <p className="metric-value text-red-500">{auditReport.summary.non_compliant}</p>
                </Card>
              </div>

              {/* Observations Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Audit Observations
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Control</th>
                          <th>Department</th>
                          <th>Status</th>
                          <th>Remark</th>
                          <th>Comment</th>
                        </tr>
                      </thead>
                      <tbody>
                        {auditReport.observations.map((obs, idx) => (
                          <tr key={idx}>
                            <td className="font-medium text-[#4A5690]">
                              {obs.control_title || obs.control_id}
                            </td>
                            <td>{obs.department_name}</td>
                            <td>{getStatusBadge(obs.compliance_status)}</td>
                            <td className="max-w-[200px] truncate">{obs.audit_remark || '-'}</td>
                            <td className="max-w-[200px] truncate">{obs.auditor_comment || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card>
              <CardContent className="py-16 text-center text-slate-500">
                <FileText size={48} className="mx-auto mb-4 text-slate-300" />
                <p>Select an audit to view the report</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Findings Register Tab */}
        <TabsContent value="findings" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Findings Register
              </CardTitle>
              {findingsRegister.length > 0 && (
                <Button
                  variant="outline"
                  onClick={() => downloadCSV(findingsRegister, 'findings-register')}
                  data-testid="download-findings-register"
                >
                  <Download size={16} className="mr-2" />
                  Export CSV
                </Button>
              )}
            </CardHeader>
            <CardContent className="p-0">
              {findingsRegister.length === 0 ? (
                <div className="py-16 text-center text-slate-500">
                  <AlertTriangle size={48} className="mx-auto mb-4 text-slate-300" />
                  <p>No findings recorded</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Control</th>
                        <th>Department</th>
                        <th>Severity</th>
                        <th>Observation</th>
                        <th>Recommendation</th>
                        <th>Status</th>
                        <th>CAPA Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {findingsRegister.map((finding, idx) => (
                        <tr key={idx}>
                          <td className="font-medium text-[#4A5690]">
                            {finding.control_title || finding.control_id}
                          </td>
                          <td>{finding.department_name}</td>
                          <td>{getSeverityBadge(finding.severity)}</td>
                          <td className="max-w-[200px] truncate">{finding.observation}</td>
                          <td className="max-w-[200px] truncate">{finding.recommendation}</td>
                          <td>{getStatusBadge(finding.status)}</td>
                          <td>
                            <Badge variant="outline">{finding.capa_status}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Consolidated Report Tab */}
        <TabsContent value="consolidated" className="space-y-6">
          <Card>
            <CardContent className="py-16 text-center text-slate-500">
              <BarChart2 size={48} className="mx-auto mb-4 text-slate-300" />
              <p className="text-lg font-medium mb-2">Consolidated Report</p>
              <p className="text-sm">View the compliance dashboard for consolidated metrics</p>
              <Button
                className="mt-4 bg-[#4A5690] hover:bg-[#3D477A]"
                onClick={() => (window.location.href = '/dashboard')}
              >
                Go to Dashboard
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReportsPage;
